import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Phone, Users, GitBranch,
  Settings, Menu, X, UserCog, LogOut
} from 'lucide-react';

const navItems = [
  { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/calls', icon: Phone, label: 'Calls' },
  { path: '/contacts', icon: Users, label: 'Contacts' },
  { path: '/pipeline', icon: GitBranch, label: 'Pipeline' },
  { path: '/team', icon: UserCog, label: 'Team' },
  { path: '/settings', icon: Settings, label: 'Settings' },
];

const bottomTabs = [
  { path: '/', icon: LayoutDashboard, label: 'Home' },
  { path: '/calls', icon: Phone, label: 'Calls' },
  { path: '/contacts', icon: Users, label: 'Contacts' },
  { path: '/team', icon: UserCog, label: 'Team' },
  { path: '/settings', icon: Settings, label: 'Settings' },
];

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const Logo = () => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
    <img
      src="/apple-touch-icon.png"
      alt="Aizaan"
      style={{ width: 32, height: 32, imageRendering: '-webkit-optimize-contrast', flexShrink: 0 }}
    />
    <div>
      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.2 }}>
        Aizaan
      </div>
      <div style={{ fontSize: 10, color: 'var(--text-muted)', lineHeight: 1.2 }}>
        Call Intelligence
      </div>
    </div>
  </div>
);

export default function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [trialDays, setTrialDays] = useState(null);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    try {
      const stored = localStorage.getItem('cia_customer');
      if (stored) setCustomer(JSON.parse(stored));
    } catch (e) {}

    const token = localStorage.getItem('cia_token');
    if (token) {
      fetch(`${API_BASE}/calls/trial-status`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(r => r.json())
        .then(data => {
          if (data.reason === 'trial_active' && data.days_remaining !== undefined) {
            setTrialDays(data.days_remaining);
          } else if (data.reason === 'trial_expired') {
            setTrialDays(-1);
          }
        })
        .catch(() => {});
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('cia_token');
    localStorage.removeItem('cia_customer');
    navigate('/login');
  };

  const customerName = customer?.name || customer?.business_name || 'Account';
  const customerInitial = customerName[0]?.toUpperCase() || 'A';

  const isActive = (path) =>
    path === '/'
      ? location.pathname === '/' || location.pathname === '/dashboard'
      : location.pathname.startsWith(path);

  const SidebarNav = ({ onNav }) => (
    <>
      {/* Trial banner */}
      {trialDays !== null && trialDays <= 7 && (
        <div style={{
          margin: '10px 10px 0',
          padding: '8px 12px',
          borderRadius: 6,
          background: trialDays < 0 ? 'rgba(239,68,68,0.1)' : 'rgba(245,158,11,0.1)',
          border: `1px solid ${trialDays < 0 ? 'rgba(239,68,68,0.3)' : 'rgba(245,158,11,0.3)'}`,
        }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: trialDays < 0 ? '#ef4444' : '#f59e0b', marginBottom: 3 }}>
            {trialDays < 0 ? 'Trial expired' : trialDays === 0 ? 'Trial expires today' : `${trialDays} days left`}
          </div>
          <Link to="/settings" style={{ fontSize: 11, color: 'var(--blue-light)' }}>
            Upgrade now →
          </Link>
        </div>
      )}

      {/* Nav links */}
      <nav style={{ flex: 1, padding: '12px 10px' }}>
        {navItems.map(item => {
          const active = isActive(item.path);
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={onNav}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '9px 12px',
                borderRadius: 8,
                marginBottom: 2,
                color: active ? 'var(--text-primary)' : 'var(--text-secondary)',
                background: active ? 'var(--bg-hover)' : 'transparent',
                fontWeight: active ? 600 : 400,
                fontSize: 13,
                transition: 'all 0.15s',
                borderLeft: active ? '2px solid var(--blue)' : '2px solid transparent',
              }}
            >
              <item.icon size={16} />
              {item.label}
            </Link>
          );
        })}
      </nav>

      {/* Customer info + logout */}
      <div style={{ padding: '12px 10px', borderTop: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 8 }}>
          <div style={{
            width: 28, height: 28,
            background: 'var(--blue)',
            borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 11, fontWeight: 700, color: 'white',
            flexShrink: 0,
          }}>
            {customerInitial}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {customerName}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'capitalize' }}>
              {customer?.plan || 'starter'} plan
            </div>
          </div>
          <button
            onClick={handleLogout}
            title="Sign out"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4, borderRadius: 4 }}
          >
            <LogOut size={14} />
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className="app-shell">

      {/* ── Desktop sidebar (hidden on mobile via CSS) ── */}
      <aside className="desktop-sidebar">
        <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid var(--border)' }}>
          <Logo />
        </div>
        <SidebarNav onNav={null} />
      </aside>

      {/* ── Main area ── */}
      <div className="main-wrapper">

        {/* Mobile top header (hidden on desktop via CSS) */}
        <header className="mobile-header">
          <Logo />
          <button
            onClick={() => setDrawerOpen(true)}
            aria-label="Open menu"
            style={{ background: 'none', color: 'var(--text-secondary)', padding: 8, borderRadius: 6 }}
          >
            <Menu size={22} />
          </button>
        </header>

        {/* Page content */}
        <main className="main-content">
          {children}
        </main>

        {/* Mobile bottom tab bar (hidden on desktop via CSS) */}
        <nav className="bottom-nav">
          {bottomTabs.map(tab => {
            const active = isActive(tab.path);
            return (
              <Link
                key={tab.path}
                to={tab.path}
                className={`bottom-nav-item${active ? ' active' : ''}`}
              >
                <tab.icon size={20} />
                {tab.label}
              </Link>
            );
          })}
        </nav>
      </div>

      {/* ── Mobile drawer overlay ── */}
      {drawerOpen && (
        <div
          className="drawer-overlay"
          onClick={() => setDrawerOpen(false)}
        >
          <div
            className="drawer"
            onClick={e => e.stopPropagation()}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '16px 16px 14px',
              borderBottom: '1px solid var(--border)',
            }}>
              <Logo />
              <button
                onClick={() => setDrawerOpen(false)}
                aria-label="Close menu"
                style={{ background: 'none', color: 'var(--text-secondary)', padding: 4 }}
              >
                <X size={18} />
              </button>
            </div>
            <SidebarNav onNav={() => setDrawerOpen(false)} />
          </div>
        </div>
      )}

    </div>
  );
}
